<template>
  <vue-html2pdf
    ref="invoicesListPdf"
    :show-layout="false"
    :float-layout="true"
    :enable-download="false"
    :preview-modal="true"
    :paginate-elements-by-height="1400"
    :filename="filename"
    :pdf-quality="2"
    :manual-pagination="false"
    pdf-format="a4"
    pdf-orientation="portrait"
    pdf-content-width="800px"
    @hasStartedGeneration="hasStartedGeneration"
    @hasGenerated="hasGenerated"
  >
    <section
      slot="pdf-content"
      dir="rtl"
      class="pdf-content"
    >

      <!-- HEADER -->
      <header class="invoice-header">
        <div>
          <div class="hospital-name">
            {{ $brand.nameArWithNumber }}
          </div>
          <div class="invoice-title">
            {{ title || $t('common.invoice-summary') }}
          </div>
        </div>

        <div class="header-meta">
          <div class="meta-row">
            <span class="label">تاريخ الطباعة:</span>
            <span>{{ formattedPrintDate }}</span>
          </div>
          <div class="meta-row">
            <span class="label">عدد الفواتير:</span>
            <span>{{ items.length }}</span>
          </div>
        </div>
      </header>

      <!-- TABLE -->
      <table class="invoice-table">
        <thead>
          <tr>
            <th>رقم الفاتورة</th>
            <th>اسم المريض</th>
            <th>الخدمة / الفحص</th>
            <th>الطبيب</th>
            <th>التاريخ</th>
            <th class="amount">
              السعر
            </th>
          </tr>
        </thead>

        <tbody>
          <tr
            v-for="item in items"
            :key="item.invoice_no || item.id"
          >
            <td>#{{ item.invoice_no }}</td>
            <td>{{ item.patient_name || patientName || '—' }}</td>
            <td>{{ item.name || '—' }}</td>
            <td>{{ item.doctor_name || '-' }}</td>
            <td>{{ formatDate(item.date || item.created_at) }}</td>
            <td class="amount">
              {{ formatPrice(getNetAmount(item)) }}
            </td>
          </tr>
        </tbody>
      </table>

      <!-- SUMMARY -->
      <section class="invoice-summary">
        <div class="summary-row">
          <span>عدد الفواتير</span>
          <span>{{ items.length }}</span>
        </div>

        <div class="summary-row total">
          <span>الإجمالي الكلي</span>
          <span>
            {{ formatPrice(totalAmount) }} {{ currencyLabel }}
          </span>
        </div>
      </section>

      <!-- FOOTER -->
      <footer class="invoice-footer">
        <div>شكراً لاستخدامكم خدماتنا</div>
        <div class="note">
          تم إنشاء هذه الفاتورة بتاريخ {{ formattedPrintDate }}
        </div>
      </footer>

    </section>
  </vue-html2pdf>
</template>

<script>
import VueHtml2pdf from 'vue-html2pdf'

export default {
  name: 'InvoicesListPdf',
  components: { VueHtml2pdf },

  props: {
    patientName: {
      type: String,
      required: true,
    },
    printDate: {
      type: [String, Date],
      default: () => new Date(),
    },
    items: {
      type: Array,
      default: () => [],
    },
    title: {
      type: String,
      default: '',
    },
    currencyLabel: {
      type: String,
      default: 'IQD',
    },
    filename: {
      type: String,
      default: 'invoices-list',
    },
  },

  computed: {
    formattedPrintDate() {
      return this.formatToYMD(new Date(this.printDate))
    },

    totalAmount() {
      // Sum net amounts (after discount if available)
      return this.items.reduce(
        (sum, item) => sum + this.getNetAmount(item),
        0,
      )
    },
  },

  methods: {
    getNetAmount(item) {
      if (!item) return 0
      const amount = Number(item.amount) || 0
      const discount = Number(item.discount) || 0
      const net = amount - discount
      return net > 0 ? net : 0
    },
    formatPrice(value) {
      return Number(value || 0).toLocaleString()
    },

    formatToYMD(date) {
      if (!(date instanceof Date)) return '----/--/--'
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}/${m}/${d}`
    },

    formatDate(value) {
      if (!value) return '—'
      return this.formatToYMD(new Date(value))
    },

    hasStartedGeneration() {
      this.$emit('hasStartedGeneration')
    },

    hasGenerated(event) {
      this.$emit('hasGenerated', event)
    },

    generatePdf() {
      if (this.$refs.invoicesListPdf) {
        this.$refs.invoicesListPdf.generatePdf()
      }
    },
  },
}
</script>

  <style scoped>

    /* =========================
       ARABIC FONT FIX (IMPORTANT)
       ========================= */

    @font-face {
      font-family: "TahomaPDF";
      src: url("/fonts/Tahoma.ttf") format("truetype");
      font-weight: normal;
      font-style: normal;
    }

    /* =========================
       BASE
       ========================= */

    .pdf-content {
      font-family:
        "TahomaPDF",
        "Tahoma",
        "Segoe UI",
        "Arial",
        "Noto Naskh Arabic",
        "Amiri",
        sans-serif;

      color: #000;
      background: #fff;
      padding: 30px;

      direction: rtl;
      unicode-bidi: embed;
      letter-spacing: normal;
    }

    /* =========================
       HEADER
       ========================= */

    .invoice-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      border-bottom: 2px solid #000;
      padding-bottom: 12px;
      margin-bottom: 20px;
    }

    .hospital-name {
      font-size: 14px;
      font-weight: bold;
    }

    .invoice-title {
      font-size: 22px;
      font-weight: bold;
      margin-top: 4px;
    }

    .header-meta {
      font-size: 13px;
    }

    .meta-row {
      display: flex;
      gap: 6px;
    }

    .label {
      font-weight: bold;
    }

    /* =========================
       TABLE
       ========================= */

    .invoice-table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 20px;
    }

    .invoice-table th,
    .invoice-table td {
      border: 1px solid #000;
      padding: 8px;
      font-size: 13px;
      text-align: right;
    }

    .invoice-table th {
      font-weight: bold;
    }

    .amount {
      text-align: left;
      direction: ltr;
      font-weight: bold;
    }

    /* =========================
       SUMMARY
       ========================= */

    .invoice-summary {
      margin-top: 20px;
      border-top: 2px solid #000;
      padding-top: 10px;
    }

    .summary-row {
      display: flex;
      justify-content: space-between;
      padding: 6px 0;
      font-size: 14px;
    }

    .summary-row.total {
      font-weight: bold;
      border-top: 1px solid #000;
      margin-top: 6px;
      padding-top: 10px;
    }

    /* =========================
       FOOTER
       ========================= */

    .invoice-footer {
      margin-top: 30px;
      border-top: 1px solid #000;
      padding-top: 10px;
      text-align: center;
      font-size: 12px;
    }

    .note {
      margin-top: 4px;
    }

    /* =========================
       PRINT
       ========================= */

    @media print {
      * {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
    }

    </style>
